import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent } from "@tabipla/ui";
import { Calendar, Clock, Wallet, Sparkles, Users, Calculator, ChevronRight } from "lucide-react";
import { LandingLayout } from "@/presentation/layouts/LandingLayout";
import { Illustration } from "@/presentation/components/Illustration";

export function Landing() {
  const { t } = useTranslation();

  return (
    <LandingLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-primary/10 py-16 sm:py-24">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">
            {/* Text Content */}
            <div className="max-w-xl text-center lg:text-left">
              <h1 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                {t("landing.hero.title")}
              </h1>
              <p className="mb-8 text-lg text-muted-foreground sm:text-xl">
                {t("landing.hero.subtitle")}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
                <Link to="/register">
                  <Button size="lg" className="w-full sm:w-auto">
                    {t("landing.hero.cta")}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Illustration - hidden on mobile, shown from sm breakpoint */}
            <div className="hidden shrink-0 sm:block">
              <Illustration
                name="travel-plans"
                alt={t("landing.hero.title")}
                className="h-64 w-auto object-contain sm:h-72 lg:h-80"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-16 sm:py-24">
        <div className="container mx-auto px-4">
          <h2 className="mb-12 text-center text-2xl font-bold sm:text-3xl">
            {t("landing.problems.title")}
          </h2>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* Problem 1: Schedule */}
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center p-6 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
                  <Calendar className="h-8 w-8 text-orange-500" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">
                  {t("landing.problems.schedule.title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("landing.problems.schedule.description")}
                </p>
              </CardContent>
            </Card>

            {/* Problem 2: Planning */}
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center p-6 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <Clock className="h-8 w-8 text-blue-500" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">
                  {t("landing.problems.planning.title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("landing.problems.planning.description")}
                </p>
              </CardContent>
            </Card>

            {/* Problem 3: Payment */}
            <Card className="border-2 border-dashed sm:col-span-2 lg:col-span-1">
              <CardContent className="flex flex-col items-center p-6 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <Wallet className="h-8 w-8 text-red-500" />
                </div>
                <h3 className="mb-2 text-lg font-semibold">
                  {t("landing.problems.payment.title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("landing.problems.payment.description")}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-muted/30 py-16 sm:py-24">
        <div className="container mx-auto max-w-5xl px-4">
          <h2 className="mb-12 text-center text-2xl font-bold sm:text-3xl">
            {t("landing.features.title")}
          </h2>

          <div className="space-y-16 lg:space-y-20">
            {/* Feature 1: AI */}
            <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="shrink-0 lg:order-2">
                <Illustration
                  name="chat-with-ai"
                  alt={t("landing.features.ai.title")}
                  className="h-48 w-auto sm:h-56 lg:h-64"
                />
              </div>
              <div className="max-w-lg text-center lg:order-1 lg:text-left">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                  <Sparkles className="h-4 w-4" />
                  AI
                </div>
                <h3 className="mb-4 text-xl font-bold sm:text-2xl">
                  {t("landing.features.ai.title")}
                </h3>
                <p className="text-muted-foreground">{t("landing.features.ai.description")}</p>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-primary">✓</span>
                    {t("landing.features.ai.point1")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-primary">✓</span>
                    {t("landing.features.ai.point2")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-primary">✓</span>
                    {t("landing.features.ai.point3")}
                  </li>
                </ul>
              </div>
            </div>

            {/* Feature 2: Schedule */}
            <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="shrink-0">
                <Illustration
                  name="team-up"
                  alt={t("landing.features.schedule.title")}
                  className="h-48 w-auto sm:h-56 lg:h-64"
                />
              </div>
              <div className="max-w-lg text-center lg:text-left">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-green-500/10 px-4 py-2 text-sm font-medium text-green-600 dark:text-green-400">
                  <Users className="h-4 w-4" />
                  {t("landing.features.schedule.badge")}
                </div>
                <h3 className="mb-4 text-xl font-bold sm:text-2xl">
                  {t("landing.features.schedule.title")}
                </h3>
                <p className="text-muted-foreground">
                  {t("landing.features.schedule.description")}
                </p>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-green-500">✓</span>
                    {t("landing.features.schedule.point1")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-green-500">✓</span>
                    {t("landing.features.schedule.point2")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-green-500">✓</span>
                    {t("landing.features.schedule.point3")}
                  </li>
                </ul>
              </div>
            </div>

            {/* Feature 3: Payment */}
            <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div className="shrink-0 lg:order-2">
                <Illustration
                  name="savings"
                  alt={t("landing.features.payment.title")}
                  className="h-48 w-auto sm:h-56 lg:h-64"
                />
              </div>
              <div className="max-w-lg text-center lg:order-1 lg:text-left">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-4 py-2 text-sm font-medium text-amber-600 dark:text-amber-400">
                  <Calculator className="h-4 w-4" />
                  {t("landing.features.payment.badge")}
                </div>
                <h3 className="mb-4 text-xl font-bold sm:text-2xl">
                  {t("landing.features.payment.title")}
                </h3>
                <p className="text-muted-foreground">{t("landing.features.payment.description")}</p>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-amber-500">✓</span>
                    {t("landing.features.payment.point1")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-amber-500">✓</span>
                    {t("landing.features.payment.point2")}
                  </li>
                  <li className="flex items-center gap-2 lg:justify-start">
                    <span className="text-amber-500">✓</span>
                    {t("landing.features.payment.point3")}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center text-center">
            <Illustration
              name="adventure"
              alt={t("landing.cta.title")}
              className="mb-8 w-full max-w-xs"
            />
            <h2 className="mb-4 text-2xl font-bold sm:text-3xl">{t("landing.cta.title")}</h2>
            <p className="mb-8 text-muted-foreground">{t("landing.cta.subtitle")}</p>
            <Link to="/register">
              <Button size="lg">
                {t("landing.hero.cta")}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </LandingLayout>
  );
}
